import React, { useState } from 'react';
import { useAPI } from '../api';
import { isAuthenticated, getAuth, updateAuth } from '../_auth.js';
import { initSDK } from '../_firebase.js';
import { navigate } from 'gatsby';
import store, { actions, getters } from 'redux/store';
import { decode } from 'js-base64';
import { writeLog, logDiscord } from '../_discordLog';
import { isInNativeApp, requestRefreshAuthToken } from '../nativeBridge';
import { callNativeAppFunc } from '../jsbridge';
import { NATIVE_APP_CODE } from '../constants/nativeAppCode.js';
import { redirectToMaintenanceIfNeeded } from '../maintenance';
import { isPublicStandalonePath } from '../routePolicy';

const REFRESH_RELOAD_KEY = 'refreshAuthReloadCount';
const MAX_RELOAD_ATTEMPTS = 3;
const REFRESH_AUTH_PARSE_FAIL_KEY = 'refreshAuthParseFailCount';

const useProvideMount = () => {
  const api = useAPI();

  useState(() => {
    const isStandalonePage =
      typeof window !== 'undefined' &&
      isPublicStandalonePath(window.location.pathname);
    store.dispatch(actions.setIsInNativeApp(isInNativeApp));
    if (!isStandalonePage) {
      if (isAuthenticated()) {
        const { qcAuth, ocaToken, ccAuth, refreshToken } = getAuth();
        api.updateToken({ qcAuth, ocaToken, ccAuth, refreshToken });
      } else {
        if (isInNativeApp && typeof window !== 'undefined') {
          if (window.location.pathname !== '/auth') {
            navigate('/auth', { replace: true });
          }
        }
      }
    }

    if (typeof window !== 'undefined') {
      initSDK();
      if (!isStandalonePage) {
        redirectToMaintenanceIfNeeded();
      }

      const resetRefreshAuthParseFailCount = () => {
        sessionStorage.removeItem(REFRESH_AUTH_PARSE_FAIL_KEY);
      };

      const handleRefreshAuthParseFailure = (
        error,
        reason = 'JSON 解析失敗'
      ) => {
        const failCount =
          parseInt(
            sessionStorage.getItem(REFRESH_AUTH_PARSE_FAIL_KEY) || '0',
            10
          ) + 1;

        sessionStorage.setItem(REFRESH_AUTH_PARSE_FAIL_KEY, String(failCount));
        writeLog(
          `[sendRefreshAuthToken] ${reason}，第 ${failCount} 次: ${
            error?.message || error
          }`
        );

        if (failCount >= MAX_RELOAD_ATTEMPTS) {
          resetRefreshAuthParseFailCount();
          alert('無法登入, 授權異常', () => {
            callNativeAppFunc('closeJSPWebview');
          });
          return;
        }

        requestRefreshAuthToken();
      };

      // 頁面正常載入後，清除重整計數器（延遲清除，確保不在 reload 迴圈中）
      setTimeout(() => {
        sessionStorage.removeItem(REFRESH_RELOAD_KEY);
        resetRefreshAuthParseFailCount();
      }, 5000);

      window.sendRefreshAuthToken = (strJson) => {
        let dataJSON;

        try {
          dataJSON = JSON.parse(decode(strJson));
          resetRefreshAuthParseFailCount();
          if (dataJSON?.code === NATIVE_APP_CODE.SUCCESS) {
            const ocaToken = dataJSON?.data?.['oca-token'] || '';
            const qcAuth = dataJSON?.data?.['qc-auth'] || '';
            const ccAuth = dataJSON?.data?.['cc-auth'] || '';
            const customerNo = dataJSON?.data?.['customer-no'] || '';
            writeLog(
              'sendRefreshAuthToken received data: ' + JSON.stringify(dataJSON)
            );
            if (customerNo && qcAuth && ccAuth && ocaToken) {
              // 檢查 Token 是否真的有變動
              const oldAuth = getAuth();
              const isTokenChanged =
                oldAuth.qcAuth !== qcAuth ||
                oldAuth.ccAuth !== ccAuth ||
                oldAuth.ocaToken !== ocaToken;

              writeLog(
                `sendRefreshAuthToken: 舊 Token: ${JSON.stringify(
                  oldAuth
                )}, 新 Token: ${JSON.stringify({
                  qcAuth,
                  ccAuth,
                  ocaToken,
                  customerNo,
                })}, 是否變動: ${isTokenChanged}`
              );
              updateAuth({
                ocaToken: ocaToken,
                qcAuth: qcAuth,
                ccAuth: ccAuth,
                customerNo: customerNo,
              });

              writeLog('axios updateToken');
              api.updateToken({
                ocaToken: ocaToken,
                qcAuth: qcAuth,
                ccAuth: ccAuth,
                customerNo: customerNo,
              });
              const userInfo = getters.getUserInfo(store.getState());
              store.dispatch(
                actions.setUserInfo({
                  ...userInfo,
                  customerNo: customerNo,
                })
              );

              // 防止無限重整：檢查 Token 是否有變動 + 重試次數限制
              const reloadCount = parseInt(
                sessionStorage.getItem(REFRESH_RELOAD_KEY) || '0',
                10
              );

              if (!isTokenChanged) {
                writeLog('sendRefreshAuthToken: Token 未變動，跳過 reload');
                console.warn(
                  '[sendRefreshAuthToken] Token 未變動，跳過 reload 以避免無限重整'
                );
                return;
              }

              if (reloadCount >= MAX_RELOAD_ATTEMPTS) {
                writeLog(
                  `sendRefreshAuthToken: 已達最大重整次數 (${MAX_RELOAD_ATTEMPTS})，停止 reload`
                );
                sessionStorage.removeItem(REFRESH_RELOAD_KEY);
                return;
              }

              sessionStorage.setItem(
                REFRESH_RELOAD_KEY,
                String(reloadCount + 1)
              );

              logDiscord();
              window.location.reload();
            }
          } else if (dataJSON?.code === NATIVE_APP_CODE.FAIL) {
            handleRefreshAuthParseFailure(
              new Error('NATIVE_APP_CODE.FAIL'),
              '授權回傳失敗'
            );
          }
        } catch (error) {
          handleRefreshAuthParseFailure(error);
        }
      };
    }
  }, []);
};

export default useProvideMount;
